import React from "react";
import { Copy } from "lucide-react";
import { Button } from "./ui/button";

function renderInline(text) {
	const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g);
	return parts.map((part, index) => {
		if (part.startsWith("`") && part.endsWith("`")) {
			return (
				<code
					key={index}
					className="rounded-sm bg-secondary px-1 py-0.5 font-mono text-[0.9em] text-volt"
				>
					{part.slice(1, -1)}
				</code>
			);
		}
		if (part.startsWith("**") && part.endsWith("**")) {
			return <strong key={index}>{part.slice(2, -2)}</strong>;
		}
		if (part.startsWith("*") && part.endsWith("*")) {
			return <em key={index}>{part.slice(1, -1)}</em>;
		}
		return <React.Fragment key={index}>{part}</React.Fragment>;
	});
}

export function AIMarkdown({ content, className = "" }) {
	if (!content) return null;

	const lines = String(content).replace(/\r\n/g, "\n").split("\n");
	const blocks = [];
	let paragraph = [];
	let list = [];
	let listType = null;
	let codeLines = null;
	let codeLanguage = "";
	let tableHeaders = null;
	let tableRows = [];

	const flushParagraph = () => {
		if (paragraph.length > 0) {
			blocks.push(
				<p key={`paragraph-${blocks.length}`} className="leading-relaxed">
					{renderInline(paragraph.join(" "))}
				</p>,
			);
			paragraph = [];
		}
	};

	const flushList = () => {
		if (list.length === 0) return;
		const ListTag = listType === "ordered" ? "ol" : "ul";
		blocks.push(
			<ListTag
				key={`list-${blocks.length}`}
				className={`space-y-1 pl-5 ${listType === "ordered" ? "list-decimal" : "list-disc"}`}
			>
				{list.map((item, index) => (
					<li key={`${listType}-${index}`}>{renderInline(item)}</li>
				))}
			</ListTag>,
		);
		list = [];
		listType = null;
	};

	const flushTable = () => {
		if (!tableHeaders) return;
		blocks.push(
			<div
				key={`table-${blocks.length}`}
				className="overflow-x-auto rounded-sm border border-border"
			>
				<table className="w-full min-w-[420px] border-collapse text-left text-xs">
					<thead className="bg-secondary text-foreground">
						<tr>
							{tableHeaders.map((header, index) => (
								<th
									key={`header-${index}`}
									className="border-b border-border px-2 py-1.5 font-medium"
								>
									{renderInline(header)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{tableRows.map((row, rowIndex) => (
							<tr
								key={`row-${rowIndex}`}
								className="border-b border-border/60 last:border-b-0"
							>
								{row.map((cell, cellIndex) => (
									<td
										key={`cell-${rowIndex}-${cellIndex}`}
										className="px-2 py-1.5 align-top text-muted-foreground"
									>
										{renderInline(cell)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>,
		);
		tableHeaders = null;
		tableRows = [];
	};

	const flushCode = () => {
		if (!codeLines) return;
		const code = codeLines.join("\n");
		blocks.push(
			<div
				key={`code-${blocks.length}`}
				className="overflow-hidden rounded-sm border border-border bg-card"
			>
				<div className="flex items-center justify-between border-b border-border/70 px-2 py-1">
					<span className="text-[10px] uppercase tracking-widest text-muted-foreground">
						{codeLanguage || "code"}
					</span>
					<Button
						variant="ghost"
						size="icon"
						className="h-7 w-7 rounded-sm"
						type="button"
						onClick={() => navigator.clipboard?.writeText(code)}
						aria-label="Copia codice"
					>
						<Copy className="h-3.5 w-3.5" />
					</Button>
				</div>
				<pre className="overflow-x-auto p-3 text-xs leading-relaxed text-foreground">
					<code>{code}</code>
				</pre>
			</div>,
		);
		codeLines = null;
		codeLanguage = "";
	};

	const flushStructuredBlock = () => {
		flushParagraph();
		flushList();
		flushTable();
	};

	lines.forEach((line) => {
		const trimmed = line.trim();
		if (codeLines) {
			if (trimmed.startsWith("```")) {
				flushCode();
			} else {
				codeLines.push(line);
			}
			return;
		}
		if (trimmed.startsWith("```")) {
			flushStructuredBlock();
			codeLines = [];
			codeLanguage = trimmed.slice(3).trim();
			return;
		}
		const tableCells =
			trimmed.startsWith("|") && trimmed.endsWith("|")
				? trimmed
						.slice(1, -1)
						.split("|")
						.map((cell) => cell.trim())
				: null;
		const isTableSeparator =
			tableCells?.length > 0 &&
			tableCells.every((cell) => /^:?-{3,}:?$/.test(cell));
		if (tableCells && !isTableSeparator) {
			flushParagraph();
			flushList();
			if (!tableHeaders) tableHeaders = tableCells;
			else tableRows.push(tableCells);
			return;
		}
		if (isTableSeparator) return;
		if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
			flushStructuredBlock();
			blocks.push(
				<hr key={`rule-${blocks.length}`} className="border-border" />,
			);
			return;
		}
		if (trimmed.startsWith(">")) {
			flushStructuredBlock();
			blocks.push(
				<blockquote
					key={`quote-${blocks.length}`}
					className="border-l-2 border-volt/60 pl-3 text-muted-foreground"
				>
					{renderInline(trimmed.replace(/^>\s?/, ""))}
				</blockquote>,
			);
			return;
		}
		const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
		const unordered = trimmed.match(/^[-*]\s+(.+)$/);
		const ordered = trimmed.match(/^\d+[.)]\s+(.+)$/);

		if (!trimmed) {
			flushParagraph();
			flushList();
			flushTable();
			return;
		}
		if (heading) {
			flushParagraph();
			flushList();
			flushTable();
			const HeadingTag = heading[1].length === 1 ? "h3" : "h4";
			blocks.push(
				<HeadingTag
					key={`heading-${blocks.length}`}
					className="font-heading text-sm uppercase tracking-wide text-foreground"
				>
					{renderInline(heading[2])}
				</HeadingTag>,
			);
			return;
		}
		if (unordered || ordered) {
			flushParagraph();
			flushTable();
			const nextType = unordered ? "unordered" : "ordered";
			if (listType && listType !== nextType) flushList();
			listType = nextType;
			list.push((unordered || ordered)[1]);
			return;
		}
		flushList();
		flushTable();
		paragraph.push(trimmed);
	});

	flushParagraph();
	flushList();
	flushTable();
	flushCode();

	return <div className={`space-y-2 text-sm ${className}`}>{blocks}</div>;
}
